import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Speech recognition for the hands-free drill.
 *
 * The important thing to know about this API: **Chrome's default recognition
 * is not local.** `webkitSpeechRecognition` streams microphone audio to
 * Google's servers, which means it needs a connection and means the learner's
 * voice leaves the machine. That is the opposite of how the rest of this app
 * works — the catalog is bundled, the audio is synthesized on device, and the
 * backend only stores progress.
 *
 * Chrome has since added an on-device path: a downloadable language pack, an
 * `available()` check and a `processLocally` flag on the recogniser. It is new
 * enough that its exact shape cannot be relied on, so every call into it is
 * feature-detected and wrapped — a browser that does not have it must degrade,
 * not throw.
 *
 * The rule this hook enforces is that `localOnly` is honoured absolutely. If
 * the learner has asked for local processing and the browser cannot do it, this
 * reports `blocked` and recognises nothing. Quietly falling back to the network
 * would break a promise the settings panel made.
 */

// ── Minimal typings ───────────────────────────────────────────────────────
// The Web Speech API is not in TypeScript's DOM lib. Only what is used here is
// declared, rather than pulling in a dependency for four interfaces.

interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}
interface SpeechRecognitionResult {
  readonly length: number;
  isFinal: boolean;
  [index: number]: SpeechRecognitionAlternative;
}
interface SpeechRecognitionResultList {
  readonly length: number;
  [index: number]: SpeechRecognitionResult;
}
interface SpeechRecognitionEventLike extends Event {
  resultIndex: number;
  results: SpeechRecognitionResultList;
}
interface SpeechRecognitionErrorEventLike extends Event {
  error: string;
  message?: string;
}
interface SpeechRecognitionLike extends EventTarget {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  /** Chrome's on-device flag. Absent on older builds. */
  processLocally?: boolean;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEventLike) => void) | null;
  onend: (() => void) | null;
  onstart: (() => void) | null;
}
interface SpeechRecognitionConstructor {
  new (): SpeechRecognitionLike;
  /** Newer Chrome. Reports whether a language can be handled, and how. */
  available?: (opts: { langs: string[]; processLocally: boolean }) => Promise<string>;
  installOnDevice?: (opts: { langs: string[] }) => Promise<boolean>;
}

function recognitionConstructor(): SpeechRecognitionConstructor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

// ── Capability ────────────────────────────────────────────────────────────

export type Processing = 'on-device' | 'network' | 'unavailable';

export interface RecognitionCapability {
  supported: boolean;
  /** Where audio would actually be processed for the requested language. */
  processing: Processing;
  /** True once an on-device language pack could be downloaded on request. */
  installable: boolean;
  checking: boolean;
}

/**
 * Works out whether recognition can run locally for a language.
 *
 * `available()` answers with a string — "available", "downloadable",
 * "downloading" or "unavailable" are the values seen in the wild. Anything
 * unrecognised is treated as "cannot do it locally", which fails safe.
 */
export function useRecognitionCapability(lang: string): RecognitionCapability & {
  install: () => Promise<boolean>;
} {
  const Ctor = recognitionConstructor();
  const supported = Boolean(Ctor);
  const [state, setState] = useState<RecognitionCapability>({
    supported,
    processing: supported ? 'network' : 'unavailable',
    installable: false,
    checking: supported,
  });

  useEffect(() => {
    let alive = true;
    if (!Ctor) {
      setState({ supported: false, processing: 'unavailable', installable: false, checking: false });
      return;
    }
    if (typeof Ctor.available !== 'function') {
      // No on-device API at all: it works, over the network.
      setState({ supported: true, processing: 'network', installable: false, checking: false });
      return;
    }

    setState((s) => ({ ...s, checking: true }));
    Ctor.available({ langs: [lang], processLocally: true })
      .then((status) => {
        if (!alive) return;
        const ready = status === 'available';
        const downloadable = status === 'downloadable' || status === 'downloading';
        setState({
          supported: true,
          processing: ready ? 'on-device' : 'network',
          installable: downloadable,
          checking: false,
        });
      })
      .catch(() => {
        // An API that exists but throws tells us nothing good; assume network.
        if (alive) {
          setState({ supported: true, processing: 'network', installable: false, checking: false });
        }
      });

    return () => {
      alive = false;
    };
  }, [Ctor, lang]);

  const install = useCallback(async () => {
    if (!Ctor || typeof Ctor.installOnDevice !== 'function') return false;
    try {
      const ok = await Ctor.installOnDevice({ langs: [lang] });
      if (ok) {
        setState((s) => ({ ...s, processing: 'on-device', installable: false }));
      }
      return ok;
    } catch {
      return false;
    }
  }, [Ctor, lang]);

  return { ...state, install };
}

// ── Listening ─────────────────────────────────────────────────────────────

export interface Heard {
  /** Every alternative the engine offered, best first. */
  alternatives: string[];
  /** Whether the engine considers this settled. */
  final: boolean;
}

export type RecognitionFailure =
  | 'not-allowed'
  | 'no-speech'
  | 'network'
  | 'local-unavailable'
  | 'unsupported'
  | 'other';

export interface Recognition {
  listening: boolean;
  /** What the engine is hearing right now, for live feedback. */
  interim: string;
  error: RecognitionFailure | null;
  /** Opens the microphone. Resolves with the best transcripts, or null. */
  listen: (lang: string, timeoutMs: number) => Promise<Heard | null>;
  stop: () => void;
}

export interface RecognitionOptions {
  /** Refuse to run at all unless processing stays on the device. */
  localOnly: boolean;
}

/**
 * How long to leave between one recognition cycle ending and the next starting.
 *
 * `start()` throws `InvalidStateError` if it lands before the previous instance
 * has finished releasing the microphone, so the restart is not immediate.
 */
const RESTART_GAP_MS = 150;

/** Detaches a recogniser so its dying callbacks cannot reach a settled promise. */
function detach(rec: SpeechRecognitionLike | null): void {
  if (!rec) return;
  rec.onresult = null;
  rec.onerror = null;
  rec.onend = null;
  rec.onstart = null;
  try {
    rec.abort();
  } catch {
    // Aborting one that never started throws in some builds; harmless.
  }
}

export function useRecognition({ localOnly }: RecognitionOptions): Recognition {
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState('');
  const [error, setError] = useState<RecognitionFailure | null>(null);

  const active = useRef<SpeechRecognitionLike | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const restartTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cleanup = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    if (restartTimer.current) {
      clearTimeout(restartTimer.current);
      restartTimer.current = null;
    }
    detach(active.current);
    active.current = null;
    setListening(false);
    setInterim('');
  }, []);

  // A drill left mid-question must not leave the microphone open.
  useEffect(() => cleanup, [cleanup]);

  const stop = useCallback(() => cleanup(), [cleanup]);

  const listen = useCallback(
    (lang: string, timeoutMs: number): Promise<Heard | null> => {
      const Ctor = recognitionConstructor();
      if (!Ctor) {
        setError('unsupported');
        return Promise.resolve(null);
      }

      cleanup();
      setError(null);

      return new Promise<Heard | null>((resolve) => {
        /**
         * The whole point of this function: the microphone stays open for the
         * time the learner was promised.
         *
         * Chrome's recogniser does not work that way on its own. With
         * `continuous = false` it ends itself as soon as it decides a phrase is
         * over — and immediately after a spoken prompt it very often decides
         * that before the learner has drawn breath, firing `onend` (or a
         * `no-speech` error) within a second. Treating that first `onend` as
         * the answer is what made every card come back wrong instantly.
         *
         * So a listen is a *window*, not a single recognition. Cycles are
         * restarted until either a final transcript arrives or the deadline
         * passes.
         */
        const deadline = Date.now() + timeoutMs;
        let settled = false;
        let best: Heard | null = null;

        const finish = (value: Heard | null) => {
          if (settled) return;
          settled = true;
          cleanup();
          resolve(value);
        };

        const startCycle = () => {
          if (settled) return;
          if (Date.now() >= deadline) {
            finish(best);
            return;
          }

          // The previous cycle's object is finished with; detach before making
          // another so its teardown cannot touch this one.
          detach(active.current);
          active.current = null;

          let rec: SpeechRecognitionLike;
          try {
            rec = new Ctor();
          } catch {
            setError('other');
            finish(best);
            return;
          }

          rec.lang = lang;
          rec.continuous = false;
          rec.interimResults = true;
          // Alternatives are free accuracy: the matcher accepts any of them, so
          // a second guess that happens to be right rescues the answer.
          rec.maxAlternatives = 5;

          if (localOnly) {
            // `processLocally` existing is not evidence: a class field declared
            // without an initializer is present and undefined, and setting an
            // unknown property on any object succeeds silently. On-device
            // support is the whole trio — the flag plus the `available()` /
            // `installOnDevice()` pair that manages the language pack — so the
            // static half is what gets asked.
            const onDeviceApi = typeof Ctor.available === 'function';
            if (!onDeviceApi || !('processLocally' in rec)) {
              // The promise made in the settings panel cannot be kept here.
              setError('local-unavailable');
              finish(null);
              return;
            }
            rec.processLocally = true;
          }

          rec.onstart = () => setListening(true);

          rec.onresult = (event) => {
            const result = event.results[event.results.length - 1];
            if (!result) return;

            const alternatives: string[] = [];
            for (let i = 0; i < result.length; i += 1) {
              const text = result[i]?.transcript?.trim();
              if (text) alternatives.push(text);
            }
            if (alternatives.length === 0) return;

            if (result.isFinal) {
              // A settled phrase ends the window early, which is what makes a
              // quick answer feel quick rather than waiting out the timer.
              best = { alternatives, final: true };
              finish(best);
            } else {
              setInterim(alternatives[0]);
              best = { alternatives, final: false };
            }
          };

          rec.onerror = (event) => {
            const code = event.error;
            // Only these two are worth abandoning the window for. Everything
            // else — above all `no-speech`, which simply means "not yet" — is
            // handled by letting onend restart the cycle.
            if (code === 'not-allowed' || code === 'service-not-allowed') {
              setError('not-allowed');
              finish(null);
            } else if (code === 'network') {
              setError('network');
              finish(best);
            }
          };

          rec.onend = () => {
            if (settled) return;
            if (best?.final) {
              finish(best);
              return;
            }
            if (Date.now() < deadline) {
              restartTimer.current = setTimeout(startCycle, RESTART_GAP_MS);
            } else {
              finish(best);
            }
          };

          active.current = rec;

          try {
            rec.start();
          } catch {
            // Usually InvalidStateError from restarting too fast. Back off and
            // try again rather than failing the whole window.
            if (Date.now() < deadline) {
              restartTimer.current = setTimeout(startCycle, RESTART_GAP_MS * 3);
            } else {
              finish(best);
            }
          }
        };

        startCycle();

        // The backstop. The engine does not reliably fire onend, and a drill
        // with no keyboard in reach cannot hang on a card forever.
        timer.current = setTimeout(() => finish(best), timeoutMs + RESTART_GAP_MS);
      });
    },
    [cleanup, localOnly],
  );

  return { listening, interim, error, listen, stop };
}
